import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const CustomersManagement = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user?.id) {
      fetchCustomers();
    }
  }, [user?.id]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers?admin_id=${user.id}`);
      // Ensure customers is always an array
      const customersData = Array.isArray(res.data) ? res.data : (res.data?.customers || []);
      setCustomers(customersData);
    } catch (err) {
      toast.error("Failed to load customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (customerId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/customers/${customerId}/status?is_active=${!currentStatus}`);
      toast.success(`Customer ${!currentStatus ? 'enabled' : 'disabled'}`);
      fetchCustomers();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="customers-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Customers</h2>
          <p className="text-muted-foreground">{customers.length} registered customers</p>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Wallet</TableHead>
              <TableHead>Rides</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell className="font-medium">{customer.name}</TableCell>
                <TableCell>
                  <div className="text-sm">{customer.email}</div>
                  <div className="text-xs text-muted-foreground">{customer.phone}</div>
                </TableCell>
                <TableCell>₹{customer.wallet_balance || 0}</TableCell>
                <TableCell>{customer.total_rides || 0}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {customer.account_status === 'deleted' ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-600">Deleted</span>
                    ) : (
                      <>
                        <Switch
                          checked={customer.is_active !== false}
                          onCheckedChange={() => toggleStatus(customer.id, customer.is_active !== false)}
                        />
                        <span className={`text-sm ${customer.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                          {customer.is_active !== false ? 'Active' : 'Disabled'}
                        </span>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell>{customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CustomersManagement;
